import { addMilliseconds } from 'date-fns';
import { getConfig } from 'helpers/appConfig';
import throttle from 'lodash/throttle';

const config = getConfig();

const MinutesToIdle = config.apmEnv === 'development' ? 1000 : 30;

const DEFAULTS = {
  storageKey: 'idle-timeout',
  timeToLive: MinutesToIdle * 60 * 1000,
  timeToWarn: 25 * 60 * 1000,
};

enum MessageTypes {
  ActivityDetected,
  Logout,
}

export class Heartbeat {
  private isAlive = false;
  private channel: BroadcastChannel;
  private storageKey: string;
  private timeToLive: number;
  private timeToWarn: number;

  // @ts-expect-error We don't actually need an initializer in the constructor
  private timeoutId: number;
  // @ts-expect-error We don't actually need an initializer in the constructor
  private pendingTimeoutAlertId: number;
  // @ts-expect-error We don't actually need an initializer in the constructor
  private lastPersistedTime: string;

  public onTimeout?: () => void;
  public onWarn?: () => void;
  public onPersist?: () => void;

  constructor(opts?: {
    storageKey?: string;
    timeToLive?: number;
    timeToWarn?: number;
  }) {
    const { storageKey, timeToLive, timeToWarn } = { ...DEFAULTS, ...opts };
    this.storageKey = storageKey;
    this.timeToLive = timeToLive;
    this.timeToWarn = timeToWarn;

    this.channel = new globalThis.BroadcastChannel('patient-app-idle');

    this.persist = throttle((silent = false) => this._persist(silent), 5000, {
      leading: true,
      trailing: true,
    });
  }

  public persist: ReturnType<typeof throttle>;

  public shutdown() {
    if (!this.isAlive) return;

    this.persist.cancel();

    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.pendingTimeoutAlertId) clearTimeout(this.pendingTimeoutAlertId);

    // window.removeEventListener('storage', this.onStorageEventHandler);
    window.removeEventListener('scroll', this.persist);
    window.removeEventListener('keydown', this.persist);
    window.removeEventListener('mousedown', this.persist);
    window.removeEventListener('touchstart', this.persist);

    localStorage.removeItem(this.storageKey);
    this.isAlive = false;
  }

  public spinup() {
    this.isAlive = true;
    this.startTimer();

    // window.addEventListener('storage', this.onStorageEventHandler);
    window.addEventListener('scroll', this.persist);
    window.addEventListener('keydown', this.persist);
    window.addEventListener('mousedown', this.persist);
    window.addEventListener('touchstart', this.persist);

    this.persist();
  }

  public clearStorage() {
    localStorage.removeItem(this.storageKey);
  }

  private _persist(silent = false) {
    this.lastPersistedTime = addMilliseconds(
      new Date(),
      this.timeToLive,
    ).toISOString();

    localStorage.setItem(this.storageKey, this.lastPersistedTime);
    this.startTimer();

    if (silent !== true) {
      this.channel.postMessage({
        messageType: MessageTypes.ActivityDetected,
        time: this.lastPersistedTime,
      });
    }

    if (this.onPersist && silent !== true) {
      this.onPersist();
    }
  }

  private startTimer(
    timeToLive = this.timeToLive,
    timeToWarn = this.timeToWarn,
  ) {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.pendingTimeoutAlertId) {
      clearTimeout(this.pendingTimeoutAlertId);
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore This type changes depending on whether it is in a testing context or browser context
    //            but is good enough here
    this.timeoutId = setTimeout(this._onTimeout, timeToLive);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore This type changes depending on whether it is in a testing context or browser context
    //            but is good enough here
    this.pendingTimeoutAlertId = setTimeout(this._onWarnTimeout, timeToWarn);

    this.channel.onmessage = (
      e: MessageEvent<{ messageType: MessageTypes; value?: string }>,
    ) => {
      const { messageType } = e.data;
      if (messageType === MessageTypes.Logout) {
        this._onTimeout();
      } else if (messageType === MessageTypes.ActivityDetected) {
        this.persist(true);
      }
    };
  }

  private _onTimeout = () => {
    this.channel.postMessage({ messageType: MessageTypes.Logout });
    this.shutdown();

    if (this.onTimeout) {
      this.onTimeout();
    }
  };

  private _onWarnTimeout = () => {
    if (this.onWarn) this.onWarn();
  };
}
